import {
  Avatar,
  Button,
  Grid,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { useNavigate, useParams } from "react-router-dom";
import { EmptyState } from "@/core/ui";
import { useContext } from "react";
import AuthContext from "@/contexts/AuthContext";

const Ranking = ({
  topThreeData,
  loading,
  error,
  isWithoutSeason,
  emptySeasonMessage,
}) => {
  const navigate = useNavigate();
  const { clubId: clubIdFromUrl } = useParams();
  const { userLoggedData } = useContext(AuthContext);
  const resolvedClubId = clubIdFromUrl || userLoggedData?.selectedClubId || null;
  const isTopThreeLoading = loading?.topThree === true;

  if (isTopThreeLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
        }}
      >
        <Skeleton variant="text" width={120} height={34} />
        <Skeleton variant="text" width={170} height={24} sx={{ mb: 1.5 }} />
        <Grid container spacing={1.2}>
          {[1, 2, 3].map((position) => (
            <Grid item xs={4} key={`skeleton-${position}`}>
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  px: 1,
                  py: position === 1 ? 1.5 : 1.2,
                  textAlign: "center",
                }}
              >
                <Skeleton
                  variant="text"
                  width={36}
                  height={30}
                  sx={{ mx: "auto", mb: 0.5 }}
                />
                <Skeleton
                  variant="circular"
                  width={position === 1 ? 58 : 50}
                  height={position === 1 ? 58 : 50}
                  sx={{ mx: "auto", mb: 0.7 }}
                />
                <Skeleton
                  variant="text"
                  width="85%"
                  height={24}
                  sx={{ mx: "auto" }}
                />
                <Skeleton
                  variant="text"
                  width={50}
                  height={20}
                  sx={{ mx: "auto" }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  if (isWithoutSeason) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2.5,
          background:
            "linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.02) 100%)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Ranking
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O top 3 aparecerá aqui quando o ranking começar
        </Typography>
        <EmptyState
          icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 34 }} />}
          title="Você ainda não entrou em um ranking"
          description={emptySeasonMessage}
        />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Ranking
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Melhores jogadores
      </Typography>
      <Grid container spacing={1.2}>
        {topThreeData.map((player) => {
          const isLeader = player.position === 1;
          return (
            <Grid item xs={4} key={player.id}>
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: isLeader ? "primary.main" : "divider",
                  borderRadius: 2,
                  px: 1,
                  py: isLeader ? 1.5 : 1.2,
                  textAlign: "center",
                  background: isLeader
                    ? "linear-gradient(180deg, rgba(25,118,210,0.16) 0%, rgba(25,118,210,0.04) 100%)"
                    : "background.paper",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    color: isLeader ? "primary.main" : "text.secondary",
                    mb: 0.5,
                  }}
                >
                  #{player.position}
                </Typography>

                <Avatar
                  src={player.avatarUrl || undefined}
                  sx={{
                    width: isLeader ? 58 : 50,
                    height: isLeader ? 58 : 50,
                    mx: "auto",
                    mb: 0.7,
                    bgcolor: isLeader ? "primary.main" : "action.selected",
                  }}
                >
                  {player.name[0]}
                </Avatar>

                <Typography
                  sx={{
                    fontWeight: isLeader ? 800 : 700,
                    lineHeight: 1.2,
                    minHeight: 32,
                  }}
                >
                  {player.name}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 700 }}
                >
                  {player.points} pts
                </Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Button
        fullWidth
        variant="text"
        startIcon={<LeaderboardIcon />}
        onClick={() =>
          navigate(
            resolvedClubId ? `/c/${resolvedClubId}/user/ranking` : "/private/user/ranking",
          )
        }
        sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
      >
        Ver ranking completo
      </Button>
    </Paper>
  );
};

export default Ranking;
